/**
 * Global light/dark colour scheme handling.
 *
 * The scheme resolves in this order:
 *   1. an explicit choice persisted in localStorage (the nav toggle),
 *   2. the device's prefers-color-scheme.
 *
 * The resolved theme is stamped on <html> as `data-theme="dark|light"` (plus
 * the legacy `dark` class) which the palette blocks in
 * assets/styles/scss/bootstrap/_root.scss key off. An inline script in
 * public/index.html applies the same logic before first paint to avoid a
 * flash of the wrong scheme.
 */
const STORAGE_KEY = "preferred-color-scheme";

export function getStoredTheme () {
  try {
    const theme = window.localStorage.getItem(STORAGE_KEY);
    return theme === "dark" || theme === "light" ? theme : null;
  } catch (e) {
    return null;
  }
}

export function getSystemTheme () {
  return window.matchMedia && window.matchMedia("(prefers-color-scheme: light)").matches ? "light" : "dark";
}

export function getCurrentTheme () {
  return document.documentElement.getAttribute("data-theme") || getStoredTheme() || getSystemTheme();
}

export function applyTheme (theme) {
  document.documentElement.setAttribute("data-theme", theme);
  document.documentElement.classList.toggle("dark", theme === "dark");
}

/** Explicit user choice: persist it and apply it. */
export function setTheme (theme) {
  try {
    window.localStorage.setItem(STORAGE_KEY, theme);
  } catch (e) { /* private mode etc. — still apply for this page */ }
  applyTheme(theme);
}

/** Apply on startup and follow live OS changes while no explicit choice is set. */
export function initTheme () {
  applyTheme(getStoredTheme() || getSystemTheme());
  const mq = window.matchMedia && window.matchMedia("(prefers-color-scheme: light)");
  if (!mq) return;
  const onChange = () => {
    if (!getStoredTheme()) applyTheme(getSystemTheme());
  };
  if (mq.addEventListener) mq.addEventListener("change", onChange);
  else if (mq.addListener) mq.addListener(onChange);
}
